import { useContext, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { faCopy, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import { wait } from 'src/utils/wait';

interface CopyButtonProps {
    successText?: string;
    objectName: string;
    waitTime?: number;
    value: string;
}

function CopyButton(props: CopyButtonProps) {
    const { successText = 'Copied!', objectName, waitTime = 2_000, value } = props;

    const { trackEvent } = useContext(AnalyticsContext);
    const { palette } = useTheme();

    const [showCopySuccess, setShowCopySuccess] = useState(false);

    const isClipboardSupported = !!navigator?.clipboard;

    const copyButtonIdToClipboard = async () => {
        trackEvent('Copy Button Clicked', {
            objectType: ObjectTypes.Button,
            objectName,
        });

        try {
            await navigator?.clipboard?.writeText(value);
            setShowCopySuccess(true);
            await wait(waitTime);
            setShowCopySuccess(false);
        } catch (error) {
            datadogRum.addError(error, {
                message: 'Error copying to clipboard',
                value,
                objectName,
            });
        }
    };

    return (
        <Tooltip
            placement="left"
            arrow
            open={showCopySuccess}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={successText}
        >
            <IconButton
                aria-label="copy"
                onClick={copyButtonIdToClipboard}
                size="small"
                disabled={!isClipboardSupported}
                sx={{ m: -1, p: 1, pl: 0, visibility: isClipboardSupported ? 'visible' : 'hidden' }}
            >
                {showCopySuccess ? (
                    <FontAwesomeIcon icon={faCheck} color={palette.success.main} />
                ) : (
                    <FontAwesomeIcon icon={faCopy} color={palette.primary.main} />
                )}
            </IconButton>
        </Tooltip>
    );
}

export default CopyButton;
